import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    Button,
    CircularProgress,
    Alert,
    Chip,
    Divider,
    IconButton,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleIcon from '@mui/icons-material/People';
import ArrowBackIcon from '@mui/icons-material/ArrowBack'; // Added for Go Back button
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useNavigate } from 'react-router-dom'; // Added for navigation

const PayRegister = ({ token, onPayrollSelect }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState(null);
    const [payrollList, setPayrollList] = useState([]);
    const [selectedPayrollId, setSelectedPayrollId] = useState('');
    const [payrollInfo, setPayrollInfo] = useState(null);
    const [infoDialogOpen, setInfoDialogOpen] = useState(false);
    const tableRef = useRef();
    const navigate = useNavigate(); // For navigation

    // API Base URL
    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

    // Use token from props or localStorage
    const authToken = token || localStorage.getItem('authToken');

    // Go Back to Dashboard function
    const handleGoBack = () => {
        navigate('/dashboard'); // Adjust the route as per your dashboard route
    };

    // Fetch payroll list for the company
    const fetchPayrollList = useCallback(async () => {
        if (!authToken) {
            setError('Authentication token missing. Please log in again.');
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${API_BASE}/get_payroll_list.php`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                }
            });

            const text = await response.text();
            let result;
            try {
                result = JSON.parse(text);
            } catch (e) {
                console.error('Non-JSON response:', text);
                throw new Error('Server returned an invalid response. Please check if the API is working correctly.');
            }

            if (!response.ok) {
                throw new Error(result.message || `HTTP error! status: ${response.status}`);
            }

            if (result.success) {
                setPayrollList(result.data);
                if (result.data.length > 0) {
                    // Auto-select the latest payroll
                    const latestPayroll = result.data[0];
                    setSelectedPayrollId(latestPayroll.payroll_id);
                    setPayrollInfo(latestPayroll);

                    if (onPayrollSelect) {
                        onPayrollSelect(latestPayroll.payroll_id);
                    }
                } else {
                    setLoading(false);
                }
            } else {
                setError(result.message || 'Failed to fetch payroll list');
                setLoading(false);
            }
        } catch (err) {
            console.error('Error fetching payroll list:', err);
            setError(err.message || 'Network error occurred');
            setLoading(false);
        }
    }, [API_BASE, authToken, onPayrollSelect]);

    // Fetch payroll register data
    const fetchPayrollRegister = useCallback(async (payrollId) => {
        if (!authToken) return;

        try {
            setLoading(true);
            setError(null);

            const response = await fetch(`${API_BASE}/get_payregister.php?payroll_id=${payrollId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                }
            });

            const text = await response.text();
            let result;
            try {
                result = JSON.parse(text);
            } catch (e) {
                console.error('Non-JSON response:', text);
                throw new Error('Server returned an invalid response. This often happens if there is a PHP error.');
            }

            if (!response.ok) {
                throw new Error(result.message || `HTTP error! status: ${response.status}`);
            }

            if (result.success) {
                setData(result.data);
            } else {
                setError(result.message || 'Failed to fetch payroll data');
            }
        } catch (err) {
            setError(err.message || 'Network error occurred');
            console.error('Error fetching payroll data:', err);
        } finally {
            setLoading(false);
        }
    }, [API_BASE, authToken]);

    // Handle payroll selection change
    const handlePayrollChange = (event) => {
        const payrollId = event.target.value;
        setSelectedPayrollId(payrollId);

        // Find the selected payroll info
        const selectedPayroll = payrollList.find(p => String(p.payroll_id) === String(payrollId));
        if (selectedPayroll) {
            setPayrollInfo(selectedPayroll);
        }

        // Fetch payroll register data
        fetchPayrollRegister(payrollId);

        if (onPayrollSelect) {
            onPayrollSelect(payrollId);
        }
    };

    useEffect(() => {
        if (authToken) {
            fetchPayrollList();
        }
    }, [authToken, fetchPayrollList]);

    // Fetch data when payroll is selected
    useEffect(() => {
        if (selectedPayrollId) {
            fetchPayrollRegister(selectedPayrollId);
        }
    }, [selectedPayrollId, fetchPayrollRegister]);

    // Format currency
    const formatCurrency = (amount) => {
        const numericAmount = typeof amount === 'number' ? amount : parseFloat(amount) || 0;
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(numericAmount);
    };

    // Safe currency formatting
    const safeFormatCurrency = (amount) => {
        return formatCurrency(amount || 0);
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    // Format days - shows decimal only when needed
    const formatDays = (days) => {
        const numDays = Number(days || 0);
        // Show 1 decimal place only if needed
        return numDays % 1 === 0 ? numDays.toString() : numDays.toFixed(1);
    };

    // Export to Excel
    const exportToExcel = async () => {
        if (!data) return;

        try {
            setExporting(true);

            // Prepare data for Excel
            const excelData = data.employees.map(emp => {
                const rowData = {
                    'Emp Code': emp.emp_code,
                    'Employee Name': `${emp.first_name} ${emp.last_name}`,
                    'Department': emp.department || '-',
                    'Designation': emp.designation || '-',
                    'PD': formatDays(emp.present_days || 0), // Fixed: Using formatDays
                    'ED': formatDays(emp.effective_days || 0), // Fixed: Using formatDays
                    'FH': formatDays(emp.first_half_days || 0), // Added First Half
                    'PL': formatDays(emp.paid_leaves || 0) // Added Paid Leaves
                };

                // Add earnings
                (data.component_columns?.earnings || []).forEach(col => {
                    rowData[col] = emp.earnings?.[col] || 0;
                });

                rowData['Gross Pay'] = emp.gross_pay;

                // Add deductions
                (data.component_columns?.deductions || []).forEach(col => {
                    rowData[col] = emp.deductions?.[col] || 0;
                });

                rowData['Total Deduction'] = emp.total_deduction;
                rowData['Net Pay'] = emp.net_pay;

                return rowData;
            });

            // Add totals row
            excelData.push({
                'Emp Code': 'TOTAL',
                'Employee Name': '',
                'Department': '',
                'Designation': '',
                'PD': '',
                'ED': '',
                'FH': '',
                'PL': '',
                ...Object.fromEntries(
                    (data.component_columns?.earnings || []).map(col => [col, ''])
                ),
                'Gross Pay': data.totals?.total_gross,
                ...Object.fromEntries(
                    (data.component_columns?.deductions || []).map(col => [col, ''])
                ),
                'Total Deduction': data.totals?.total_deduction,
                'Net Pay': data.totals?.total_net
            });

            // Create worksheet
            const ws = XLSX.utils.json_to_sheet(excelData);

            // Style header row
            const range = XLSX.utils.decode_range(ws['!ref']);
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const address = XLSX.utils.encode_cell({ r: range.s.r, c: C });
                if (!ws[address]) continue;
                ws[address].s = {
                    font: { bold: true, color: { rgb: "FFFFFF" } },
                    fill: { fgColor: { rgb: "2E7D32" } },
                    alignment: { horizontal: "center" }
                };
            }

            // Style totals row
            const totalsRow = excelData.length - 1;
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const address = XLSX.utils.encode_cell({ r: totalsRow, c: C });
                if (!ws[address]) continue;
                ws[address].s = {
                    font: { bold: true },
                    fill: { fgColor: { rgb: "FFEB3B" } }
                };
            }

            // Set column widths
            const wscols = [
                { wch: 10 }, // Emp Code
                { wch: 25 }, // Employee Name
                { wch: 20 }, // Department
                { wch: 20 }, // Designation
                { wch: 8 },  // PD
                { wch: 8 },  // ED
                { wch: 8 },  // FH
                { wch: 8 },  // PL
                ...(data.component_columns?.earnings || []).map(() => ({ wch: 15 })),
                { wch: 15 }, // Gross Pay
                ...(data.component_columns?.deductions || []).map(() => ({ wch: 15 })),
                { wch: 15 }, // Total Deduction
                { wch: 15 }, // Net Pay
            ];
            ws['!cols'] = wscols;

            // Create workbook
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Payroll Register');

            // Add summary sheet
            const summaryData = [
                ['Payroll Summary', ''],
                ['Payroll Period:', data.payroll.month_year],
                ['From Date:', formatDate(data.payroll.from_date)],
                ['To Date:', formatDate(data.payroll.to_date)],
                ['Status:', data.payroll.status],
                ['', ''],
                ['Summary Totals', ''],
                ['Total Employees:', data.totals.total_employees],
                ['Total Gross Pay:', data.totals.total_gross],
                ['Total Deductions:', data.totals.total_deduction],
                ['Total Net Pay:', data.totals.total_net],
            ];

            const ws2 = XLSX.utils.aoa_to_sheet(summaryData);
            XLSX.utils.book_append_sheet(wb, ws2, 'Summary');

            // Generate and download file
            const fileName = `Payroll_Register_${data.payroll.month_year}_${data.payroll.payroll_id}.xlsx`;
            XLSX.writeFile(wb, fileName);

        } catch (err) {
            console.error('Error exporting to Excel:', err);
            alert('Failed to export to Excel');
        } finally {
            setExporting(false);
        }
    };

    // Corrected exportToPDF function
    const exportToPDF = () => {
        if (!data) return;

        try {
            setExporting(true);

            // Use landscape mode with larger margins
            const doc = new jsPDF('landscape');
            const pageWidth = doc.internal.pageSize.getWidth();
            const margin = 15;
            let yPos = margin;

            // Title with larger font
            doc.setFontSize(18);
            doc.setFont('helvetica', 'bold');
            doc.text('PAYROLL REGISTER', pageWidth / 2, yPos, { align: 'center' });
            yPos += 10;

            // Company Info
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text(`Company: ${data.company.company_name}`, margin, yPos);
            doc.text(`Period: ${data.payroll.month_year}`, pageWidth - margin, yPos, { align: 'right' });
            yPos += 6;
            doc.text(`Payroll ID: ${data.payroll.payroll_id}`, margin, yPos);
            doc.text(`From: ${formatDate(data.payroll.from_date)} To: ${formatDate(data.payroll.to_date)}`,
                pageWidth - margin, yPos, { align: 'right' });
            yPos += 15;

            // Calculate column widths dynamically
            const columns = [
                { header: 'Emp Code', dataKey: 'emp_code', width: 20 },
                { header: 'Employee Name', dataKey: 'employee_name', width: 40 },
                { header: 'Department', dataKey: 'department', width: 30 },
                { header: 'Designation', dataKey: 'designation', width: 30 },
                { header: 'PD', dataKey: 'present_days', width: 15 },
                { header: 'ED', dataKey: 'effective_days', width: 15 },
                { header: 'FH', dataKey: 'first_half', width: 15 },
                { header: 'PL', dataKey: 'paid_leaves', width: 15 },
            ];

            // Add earnings columns
            const earningCols = data.component_columns?.earnings || [];
            earningCols.forEach(col => {
                columns.push({
                    header: col,
                    dataKey: `earnings_${col}`,
                    width: 25
                });
            });

            columns.push({ header: 'Gross Pay', dataKey: 'gross_pay', width: 30 });

            // Add deductions columns
            const deductionCols = data.component_columns?.deductions || [];
            deductionCols.forEach(col => {
                columns.push({
                    header: col,
                    dataKey: `deductions_${col}`,
                    width: 25
                });
            });

            columns.push(
                { header: 'Total Deduction', dataKey: 'total_deduction', width: 30 },
                { header: 'Net Pay', dataKey: 'net_pay', width: 30 }
            );

            // Prepare data for autotable
            const tableData = data.employees.map(emp => {
                const row = {
                    emp_code: emp.emp_code,
                    employee_name: `${emp.first_name} ${emp.last_name}`,
                    department: emp.department || '-',
                    designation: emp.designation || '-',
                    present_days: formatDays(emp.present_days || 0), // Fixed: Using formatDays
                    effective_days: formatDays(emp.effective_days || 0), // Fixed: Using formatDays
                    first_half: formatDays(emp.first_half_days || 0), // Fixed: Using formatDays
                    paid_leaves: formatDays(emp.paid_leaves || 0), // Fixed: Using formatDays
                    gross_pay: formatCurrency(emp.gross_pay).replace('₹', '').trim(),
                    total_deduction: formatCurrency(emp.total_deduction).replace('₹', '').trim(),
                    net_pay: formatCurrency(emp.net_pay).replace('₹', '').trim()
                };

                // Add earnings
                earningCols.forEach(col => {
                    row[`earnings_${col}`] = formatCurrency(emp.earnings?.[col] || 0).replace('₹', '').trim();
                });

                // Add deductions
                deductionCols.forEach(col => {
                    row[`deductions_${col}`] = formatCurrency(emp.deductions?.[col] || 0).replace('₹', '').trim();
                });

                return row;
            });

            // Add totals row
            const totalsRow = {
                emp_code: 'TOTAL',
                employee_name: '',
                department: '',
                designation: '',
                present_days: '',
                effective_days: '',
                first_half: '',
                paid_leaves: '',
                gross_pay: formatCurrency(data.totals?.total_gross || 0).replace('₹', '').trim(),
                total_deduction: formatCurrency(data.totals?.total_deduction || 0).replace('₹', '').trim(),
                net_pay: formatCurrency(data.totals?.total_net || 0).replace('₹', '').trim()
            };

            // Add earnings totals
            earningCols.forEach(col => {
                const total = data.totals?.total_by_earning_component?.[col] || 0;
                totalsRow[`earnings_${col}`] = formatCurrency(total).replace('₹', '').trim();
            });

            // Add deductions totals
            deductionCols.forEach(col => {
                const total = data.totals?.total_by_deduction_component?.[col] || 0;
                totalsRow[`deductions_${col}`] = formatCurrency(total).replace('₹', '').trim();
            });

            tableData.push(totalsRow);

            // Use autotable with better configuration
            doc.autoTable({
                startY: yPos,
                head: [columns.map(col => col.header)],
                body: tableData.map(row =>
                    columns.map(col => row[col.dataKey] || '')
                ),
                columns: columns.map(col => ({
                    dataKey: col.dataKey,
                    header: col.header,
                    width: col.width
                })),
                margin: { left: margin, right: margin },
                tableWidth: 'auto',
                styles: {
                    fontSize: 7,
                    cellPadding: 2,
                    overflow: 'linebreak',
                    halign: 'center'
                },
                headStyles: {
                    fillColor: [41, 128, 185],
                    textColor: 255,
                    fontStyle: 'bold',
                    fontSize: 8,
                    halign: 'center'
                },
                bodyStyles: {
                    textColor: 0,
                    fontSize: 7
                },
                alternateRowStyles: {
                    fillColor: [245, 245, 245]
                },
                columnStyles: {
                    // Align specific columns
                    emp_code: { halign: 'center' },
                    employee_name: { halign: 'left' },
                    department: { halign: 'center' },
                    designation: { halign: 'center' },
                    present_days: { halign: 'center' },
                    effective_days: { halign: 'center' },
                    first_half: { halign: 'center' },
                    paid_leaves: { halign: 'center' },
                    // Align all currency columns to right
                    ...Object.fromEntries(
                        [
                            ...earningCols.map(col => `earnings_${col}`),
                            'gross_pay',
                            ...deductionCols.map(col => `deductions_${col}`),
                            'total_deduction',
                            'net_pay'
                        ].map(key => [key, { halign: 'right' }])
                    )
                },
                willDrawCell: function (HookData) {
                    // Highlight totals row
                    if (HookData.row.index === tableData.length - 1) {
                        HookData.cell.styles.fontStyle = 'bold';
                        HookData.cell.styles.fillColor = [230, 230, 230];
                    }

                    // Color coding for sections
                    const columnIndex = HookData.column.index;
                    const employeeCols = 8; // First 8 columns are employee info (including new FH and PL)
                    const earningsColsCount = earningCols.length;

                    if (HookData.row.index === 0) {
                        // Header row coloring
                        if (columnIndex >= employeeCols && columnIndex < employeeCols + earningsColsCount + 1) {
                            // Earnings section (including Gross Pay)
                            HookData.cell.styles.fillColor = [232, 245, 233];
                        } else if (columnIndex >= employeeCols + earningsColsCount + 1) {
                            // Deductions section
                            HookData.cell.styles.fillColor = [255, 235, 238];
                        }
                    }
                }
            });

            // Get final Y position after table
            const finalY = doc.lastAutoTable.finalY || yPos + 100;

            // Footer
            doc.setFontSize(8);
            doc.setTextColor(128, 128, 128);
            doc.setFont('helvetica', 'normal');
            doc.text(
                `Generated on ${new Date().toLocaleDateString('en-IN')} | ` +
                `Total Employees: ${data.totals.total_employees} | ` +
                `Payroll ID: ${data.payroll.payroll_id}`,
                pageWidth / 2,
                finalY + 10,
                { align: 'center' }
            );

            // Save PDF with safe filename
            const safeMonthYear = data.payroll.month_year.replace(/[\\:*?"<>|]/g, '_');
            const fileName = `Payroll_Register_${safeMonthYear}_${data.payroll.payroll_id}.pdf`;
            doc.save(fileName);

        } catch (err) {
            console.error('Error exporting to PDF:', err);
            alert(`Failed to export to PDF: ${err.message}`);
        } finally {
            setExporting(false);
        }
    };

    // Print function - COMPLETELY REWRITTEN to build table from scratch
    const handlePrint = () => {
        if (!data) return;

        // Create a new window for printing
        const printWindow = window.open('', '_blank', 'width=1200,height=800');

        // Build the table HTML from scratch with inline styles
        const buildTableHTML = () => {
            let tableHTML = '<table style="width: 100%; border-collapse: collapse; font-size: 11px; border: 1px solid #000;">';

            // Table Header
            tableHTML += `
                <thead>
                    <tr style="background-color: #e9ecef;">
                        <th align="center" rowspan="2" style="font-weight: bold; border: 1px solid #000; background-color: #f8f9fa; padding: 4px 6px; min-width: 80px;">Emp Code</th>
                        <th align="center" rowspan="2" style="font-weight: bold; border: 1px solid #000; background-color: #f8f9fa; padding: 4px 6px; min-width: 150px;">Employee Name</th>
                        <th align="center" rowspan="2" style="font-weight: bold; border: 1px solid #000; background-color: #f8f9fa; padding: 4px 6px; min-width: 120px;">Department</th>
                        <th align="center" rowspan="2" style="font-weight: bold; border: 1px solid #000; background-color: #f8f9fa; padding: 4px 6px; min-width: 120px;">Designation</th>
                        <th align="center" rowspan="2" style="font-weight: bold; border: 1px solid #000; background-color: #f8f9fa; padding: 4px 6px; min-width: 50px;">PD</th>
                        <th align="center" rowspan="2" style="font-weight: bold; border: 1px solid #000; background-color: #f8f9fa; padding: 4px 6px; min-width: 50px;">ED</th>
                        <th align="center" rowspan="2" style="font-weight: bold; border: 1px solid #000; background-color: #f8f9fa; padding: 4px 6px; min-width: 50px;">FH</th>
                        <th align="center" rowspan="2" style="font-weight: bold; border: 1px solid #000; background-color: #f8f9fa; padding: 4px 6px; min-width: 50px;">PL</th>`;

            // Earnings Header
            if ((data.component_columns?.earnings?.length || 0) > 0) {
                tableHTML += `<th align="center" colspan="${data.component_columns.earnings.length + 1}" style="font-weight: bold; border: 1px solid #000; background-color: #d4edda; padding: 4px 6px;">EARNINGS</th>`;
            }

            // Deductions Header
            if ((data.component_columns?.deductions?.length || 0) > 0) {
                tableHTML += `<th align="center" colspan="${data.component_columns.deductions.length + 2}" style="font-weight: bold; border: 1px solid #000; background-color: #f8d7da; padding: 4px 6px;">DEDUCTIONS</th>`;
            }

            tableHTML += `</tr><tr style="background-color: #f8f9fa;">`;

            // Earnings Sub-headers
            (data.component_columns?.earnings || []).forEach((col, index) => {
                tableHTML += `<th align="center" style="font-weight: bold; border: 1px solid #000; background-color: #e8f5e8; padding: 4px 6px; min-width: 100px;">${col}</th>`;
            });

            tableHTML += `<th align="center" style="font-weight: bold; border: 1px solid #000; background-color: #c8e6c9; padding: 4px 6px; min-width: 120px;">Gross Pay</th>`;

            // Deductions Sub-headers
            (data.component_columns?.deductions || []).forEach((col, index) => {
                tableHTML += `<th align="center" style="font-weight: bold; border: 1px solid #000; background-color: #ffebee; padding: 4px 6px; min-width: 100px;">${col}</th>`;
            });

            tableHTML += `
                <th align="center" style="font-weight: bold; border: 1px solid #000; background-color: #ffccbc; padding: 4px 6px; min-width: 120px;">Total Deduction</th>
                <th align="center" style="font-weight: bold; border: 1px solid #000; background-color: #bbdefb; padding: 4px 6px; min-width: 120px;">Net Pay</th>
            </tr>
        </thead>
        <tbody>`;

            // Employee Rows
            data.employees.forEach((emp, empIndex) => {
                const rowBgColor = empIndex % 2 === 0 ? '#fafafa' : '#ffffff';
                tableHTML += `
                <tr style="background-color: ${rowBgColor};">
                    <td align="center" style="border: 1px solid #000; padding: 4px 6px;">${emp.emp_code}</td>
                    <td style="border: 1px solid #000; padding: 4px 6px;">${emp.first_name} ${emp.last_name}</td>
                    <td align="center" style="border: 1px solid #000; padding: 4px 6px;">${emp.department || '-'}</td>
                    <td align="center" style="border: 1px solid #000; padding: 4px 6px;">${emp.designation || '-'}</td>
                    <td align="center" style="border: 1px solid #000; padding: 4px 6px;">${formatDays(emp.present_days || 0)}</td>
                    <td align="center" style="border: 1px solid #000; padding: 4px 6px;">${formatDays(emp.effective_days || 0)}</td>
                    <td align="center" style="border: 1px solid #000; padding: 4px 6px;">${formatDays(emp.first_half_days || 0)}</td>
                    <td align="center" style="border: 1px solid #000; padding: 4px 6px;">${formatDays(emp.paid_leaves || 0)}</td>`;

                // Earnings Cells
                (data.component_columns?.earnings || []).forEach((col, colIndex) => {
                    tableHTML += `<td align="right" style="border: 1px solid #000; background-color: #f9fbe7; padding: 4px 6px;">${formatCurrency(emp.earnings?.[col] || 0)}</td>`;
                });

                tableHTML += `<td align="right" style="border: 1px solid #000; background-color: #e8f5e8; font-weight: bold; padding: 4px 6px;">${formatCurrency(emp.gross_pay)}</td>`;

                // Deductions Cells
                (data.component_columns?.deductions || []).forEach((col, colIndex) => {
                    tableHTML += `<td align="right" style="border: 1px solid #000; background-color: #fff3e0; padding: 4px 6px;">${formatCurrency(emp.deductions?.[col] || 0)}</td>`;
                });

                tableHTML += `
                    <td align="right" style="border: 1px solid #000; background-color: #ffebee; font-weight: bold; padding: 4px 6px;">${formatCurrency(emp.total_deduction)}</td>
                    <td align="right" style="border: 1px solid #000; background-color: #e3f2fd; font-weight: bold; padding: 4px 6px;">${formatCurrency(emp.net_pay)}</td>
                </tr>`;
            });

            // Totals Row
            tableHTML += `
                <tr style="background-color: #e0e0e0; font-weight: bold;">
                    <td align="center" colspan="8" style="border: 1px solid #000; padding: 4px 6px;">TOTAL</td>`;

            // Earnings Totals
            (data.component_columns?.earnings || []).forEach((col, colIndex) => {
                const total = data.totals?.total_by_earning_component?.[col] || 0;
                tableHTML += `<td align="right" style="border: 1px solid #000; background-color: #c8e6c9; padding: 4px 6px;">${formatCurrency(total)}</td>`;
            });

            tableHTML += `<td align="right" style="border: 1px solid #000; background-color: #a5d6a7; padding: 4px 6px;">${formatCurrency(data.totals?.total_gross)}</td>`;

            // Deductions Totals
            (data.component_columns?.deductions || []).forEach((col, colIndex) => {
                const total = data.totals?.total_by_deduction_component?.[col] || 0;
                tableHTML += `<td align="right" style="border: 1px solid #000; background-color: #ffccbc; padding: 4px 6px;">${formatCurrency(total)}</td>`;
            });

            tableHTML += `
                <td align="right" style="border: 1px solid #000; background-color: #ffab91; padding: 4px 6px;">${formatCurrency(data.totals?.total_deduction)}</td>
                <td align="right" style="border: 1px solid #000; background-color: #90caf9; color: #0d47a1; padding: 4px 6px;">${formatCurrency(data.totals?.total_net)}</td>
            </tr>`;

            tableHTML += '</tbody></table>';
            return tableHTML;
        };

        // Create the HTML for printing
        const html = `
<!DOCTYPE html>
<html>
<head>
    <title>Payroll Register - ${data.payroll.month_year} (ID: ${data.payroll.payroll_id})</title>
    <style>
        @media print {
            @page { 
                size: landscape; 
                margin: 0.5cm; 
            }
            body { 
                font-family: Arial, sans-serif; 
                margin: 0;
                padding: 10px;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
            }
            table { 
                page-break-inside: auto; 
            }
            tr { 
                page-break-inside: avoid; 
                page-break-after: auto; 
            }
        }
        body { 
            font-family: Arial, sans-serif; 
            margin: 0;
            padding: 10px;
            background-color: white;
        }
        .print-header {
            text-align: center;
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 2px solid #000;
        }
        .company-name {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 4px;
            color: #000;
        }
        .report-title {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 8px;
            color: #1976d2;
        }
        .payroll-info {
            font-size: 12px;
            color: #333;
            margin-bottom: 4px;
        }
        .print-footer {
            margin-top: 15px;
            padding-top: 8px;
            border-top: 1px solid #ccc;
            font-size: 10px;
            color: #666;
            text-align: center;
        }
        table {
            width: 100%;
            border-collapse: collapse;
            font-size: 10px;
            margin-top: 10px;
            border: 1px solid #000 !important;
        }
        th, td {
            border: 1px solid #000 !important;
            padding: 4px 6px !important;
            text-align: center !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
        }
        * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
        }
    </style>
</head>
<body>
    <div class="print-header">
        <div class="company-name">${data.company.company_name}</div>
        <div class="report-title">PAYROLL REGISTER</div>
        <div class="payroll-info">
            Period: ${data.payroll.month_year} | 
            From: ${formatDate(data.payroll.from_date)} To: ${formatDate(data.payroll.to_date)} | 
            ID: ${data.payroll.payroll_id}
        </div>
        <div class="payroll-info">
            ${data.company.address ? data.company.address + ', ' : ''}
            ${data.company.city ? data.company.city + ', ' : ''}
            ${data.company.state ? data.company.state + ', ' : ''}
            ${data.company.country || ''}
        </div>
    </div>
    
    ${buildTableHTML()}
    
    <div class="print-footer">
        Generated on ${new Date().toLocaleDateString('en-IN')} | 
        Total Employees: ${data.totals.total_employees} | 
        Total Net Pay: ${formatCurrency(data.totals.total_net)}
    </div>
    
    <script>
        window.onload = function() {
            // Add a small delay to ensure all styles are loaded
            setTimeout(function() {
                window.print();
                setTimeout(function() {
                    window.close();
                }, 100);
            }, 100);
        }
    </script>
</body>
</html>
        `;

        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();
    };

    // Get status color
    const getStatusColor = (status) => {
        switch (status) {
            case 'Processed': return 'success';
            case 'Closed': return 'error';
            case 'Open': return 'warning';
            default: return 'default';
        }
    };

    // Loading state
    if (loading && !selectedPayrollId) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    // Error state
    if (error) {
        return (
            <Alert
                severity="error"
                action={
                    <Button color="inherit" size="small" onClick={() => fetchPayrollList()}>
                        Retry
                    </Button>
                }
            >
                {error}
            </Alert>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            {/* Payroll Selection Header */}
            <Card sx={{ mb: 3, backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={6}>
                            {/* Go Back Button */}
                            <Button
                                startIcon={<ArrowBackIcon />}
                                onClick={handleGoBack}
                                sx={{ mb: 2 }}
                                variant="outlined"
                                size="small"
                            >
                                Go Back to Dashboard
                            </Button>

                            <Typography variant="h5" sx={{ color: '#1976d2', fontWeight: 'bold', mb: 1 }}>
                                PAYROLL REGISTER
                            </Typography>

                            <FormControl fullWidth size="small">
                                <InputLabel>Select Payroll Period</InputLabel>
                                <Select
                                    value={selectedPayrollId}
                                    onChange={handlePayrollChange}
                                    label="Select Payroll Period"
                                    disabled={payrollList.length === 0}
                                >
                                    {payrollList.map((payroll) => (
                                        <MenuItem key={payroll.payroll_id} value={payroll.payroll_id}>
                                            {payroll.month_year}
                                            {payroll.unit_name ? ` - ${payroll.unit_name}` : ''}
                                            {payroll.status !== 'Processed' ? ` (${payroll.status})` : ''}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {payrollList.length === 0 && !loading && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    No payroll periods found. Process a payroll first.
                                </Alert>
                            )}
                        </Grid>

                        <Grid item xs={12} md={6}>
                            {payrollInfo && (
                                <Box sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 2, md: 0 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1 }}>
                                        <Chip
                                            label={`ID: ${payrollInfo.payroll_id}`}
                                            size="small"
                                            sx={{ mr: 1 }}
                                        />
                                        <Chip
                                            label={payrollInfo.status}
                                            color={getStatusColor(payrollInfo.status)}
                                            size="small"
                                        />
                                    </Box>

                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                        {payrollInfo.month_year}
                                    </Typography>

                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 0.5 }}>
                                        <CalendarMonthIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                                        <Typography variant="body2" color="text.secondary">
                                            {formatDate(payrollInfo.from_date)} to {formatDate(payrollInfo.to_date)}
                                        </Typography>
                                    </Box>

                                    {payrollInfo.unit_name && (
                                        <Typography variant="body2" color="text.secondary">
                                            Unit: {payrollInfo.unit_name}
                                        </Typography>
                                    )}

                                    <Button
                                        size="small"
                                        startIcon={<InfoIcon />}
                                        onClick={() => setInfoDialogOpen(true)}
                                        sx={{ mt: 1 }}
                                    >
                                        View Details
                                    </Button>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Payroll Info Dialog */}
            <Dialog open={infoDialogOpen} onClose={() => setInfoDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Payroll Information</DialogTitle>
                <DialogContent>
                    {data && (
                        <Box>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Payroll ID</Typography>
                                    <Typography variant="body1">{data.payroll.payroll_id}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                                    <Chip
                                        label={data.payroll.status}
                                        color={getStatusColor(data.payroll.status)}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Period</Typography>
                                    <Typography variant="body1">{data.payroll.month_year}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Unit</Typography>
                                    <Typography variant="body1">{data.payroll.unit_name || 'N/A'}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="text.secondary">From Date</Typography>
                                    <Typography variant="body1">{formatDate(data.payroll.from_date)}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="text.secondary">To Date</Typography>
                                    <Typography variant="body1">{formatDate(data.payroll.to_date)}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="subtitle2" color="text.secondary">Processed By</Typography>
                                    <Typography variant="body1">{data.payroll.processed_by_name || 'N/A'}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="text.secondary">Processed On</Typography>
                                    <Typography variant="body1">{formatDate(data.payroll.processed_on)}</Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setInfoDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Loading state for data */}
            {loading && selectedPayrollId && (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                </Box>
            )}

            {/* Main Content - only show when data is loaded and valid */}
            {!loading && data && data.employees && (
                <Box ref={tableRef}>
                    {/* Action Buttons */}
                    <Box sx={{
                        mb: 2,
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'flex-end',
                        flexWrap: 'wrap'
                    }}>
                        <Tooltip title="Refresh Data">
                            <IconButton
                                color="primary"
                                onClick={() => fetchPayrollRegister(selectedPayrollId)}
                                disabled={loading || exporting}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>

                        <Button
                            variant="contained"
                            startIcon={<PrintIcon />}
                            onClick={handlePrint}
                            disabled={exporting}
                            sx={{
                                backgroundColor: '#1976d2',
                                '&:hover': { backgroundColor: '#1565c0' }
                            }}
                            className="no-print"
                        >
                            Print
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={<DescriptionIcon />}
                            onClick={exportToExcel}
                            disabled={exporting}
                            sx={{
                                backgroundColor: '#2e7d32',
                                '&:hover': { backgroundColor: '#1b5e20' }
                            }}
                            className="no-print"
                        >
                            {exporting ? 'Exporting...' : 'Excel'}
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={<PictureAsPdfIcon />}
                            onClick={exportToPDF}
                            disabled={exporting}
                            sx={{
                                backgroundColor: '#d32f2f',
                                '&:hover': { backgroundColor: '#b71c1c' }
                            }}
                            className="no-print"
                        >
                            {exporting ? 'Exporting...' : 'PDF'}
                        </Button>
                    </Box>

                    {/* Company Info Card */}
                    <Card sx={{ mb: 2, backgroundColor: '#e3f2fd', border: '1px solid #bbdefb' }}>
                        <CardContent>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6" sx={{ color: '#1976d2', mb: 1 }}>
                                        {data.company?.company_name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {data.company?.company_code && `Code: ${data.company.company_code} | `}
                                        GST: {data.company?.gst_number || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {data.company?.address && `${data.company.address}, `}
                                        {data.company?.city && `${data.company.city}, `}
                                        {data.company?.state && `${data.company.state}, `}
                                        {data.company?.country}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Payroll Summary
                                        </Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
                                            <Box sx={{ textAlign: 'center' }}>
                                                <PeopleIcon fontSize="small" color="primary" />
                                                <Typography variant="caption" display="block">
                                                    {(data.totals?.total_employees || 0)} Employees
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'center' }}>
                                                <CalendarMonthIcon fontSize="small" color="primary" />
                                                <Typography variant="caption" display="block">
                                                    Present Days: {formatDays(data.totals.days_info?.present_days || 0)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Payroll Register Table */}
                    {data.employees && data.employees.length > 0 ? (
                        <>
                            <TableContainer
                                component={Paper}
                                sx={{
                                    maxHeight: 'calc(100vh - 400px)',
                                    overflow: 'auto',
                                    border: '1px solid #e0e0e0',
                                    mb: 3
                                }}
                            >
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#e9ecef' }}>
                                            {/* Employee Info */}
                                            <TableCell
                                                align="center"
                                                rowSpan={2}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    borderRight: '2px solid #dee2e6',
                                                    minWidth: 80,
                                                    backgroundColor: '#f8f9fa'
                                                }}
                                            >
                                                Emp Code
                                            </TableCell>

                                            <TableCell
                                                align="center"
                                                rowSpan={2}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    borderRight: '2px solid #dee2e6',
                                                    minWidth: 150,
                                                    backgroundColor: '#f8f9fa'
                                                }}
                                            >
                                                Employee Name
                                            </TableCell>

                                            <TableCell
                                                align="center"
                                                rowSpan={2}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    borderRight: '2px solid #dee2e6',
                                                    minWidth: 120,
                                                    backgroundColor: '#f8f9fa'
                                                }}
                                            >
                                                Department
                                            </TableCell>

                                            <TableCell
                                                align="center"
                                                rowSpan={2}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    borderRight: '2px solid #dee2e6',
                                                    minWidth: 120,
                                                    backgroundColor: '#f8f9fa'
                                                }}
                                            >
                                                Designation
                                            </TableCell>

                                            {/* PD Column */}
                                            <TableCell
                                                align="center"
                                                rowSpan={2}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    borderRight: '1px solid #dee2e6',
                                                    minWidth: 50,
                                                    backgroundColor: '#f8f9fa'
                                                }}
                                            >
                                                PD
                                            </TableCell>

                                            {/* ED Column */}
                                            <TableCell
                                                align="center"
                                                rowSpan={2}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    borderRight: '1px solid #dee2e6',
                                                    minWidth: 50,
                                                    backgroundColor: '#f8f9fa'
                                                }}
                                            >
                                                ED
                                            </TableCell>

                                            {/* FH Column */}
                                            <TableCell
                                                align="center"
                                                rowSpan={2}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    borderRight: '1px solid #dee2e6',
                                                    minWidth: 50,
                                                    backgroundColor: '#f8f9fa'
                                                }}
                                            >
                                                FH
                                            </TableCell>

                                            {/* PL Column */}
                                            <TableCell
                                                align="center"
                                                rowSpan={2}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    borderRight: '2px solid #dee2e6',
                                                    minWidth: 50,
                                                    backgroundColor: '#f8f9fa'
                                                }}
                                            >
                                                PL
                                            </TableCell>

                                            {/* Earnings Section */}
                                            {data.component_columns?.earnings?.length > 0 && (
                                                <TableCell
                                                    align="center"
                                                    colSpan={(data.component_columns?.earnings?.length || 0) + 1}
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        backgroundColor: '#d4edda',
                                                        borderRight: '2px solid #dee2e6'
                                                    }}
                                                >
                                                    EARNINGS
                                                </TableCell>
                                            )}

                                            {/* Deductions Section */}
                                            {data.component_columns?.deductions?.length > 0 && (
                                                <TableCell
                                                    align="center"
                                                    colSpan={(data.component_columns?.deductions?.length || 0) + 2}
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        backgroundColor: '#f8d7da'
                                                    }}
                                                >
                                                    DEDUCTIONS
                                                </TableCell>
                                            )}

                                            {/* Actions Section */}
                                            <TableCell
                                                align="center"
                                                rowSpan={2}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    backgroundColor: '#fff3e0',
                                                    minWidth: 100,
                                                    borderLeft: '2px solid #dee2e6'
                                                }}
                                            >
                                                Actions
                                            </TableCell>
                                        </TableRow>

                                        {/* Sub-headers */}
                                        <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                                            {/* Earnings Sub-headers */}
                                            {data.component_columns?.earnings?.map((col, index) => (
                                                <TableCell
                                                    key={`earn-${index}`}
                                                    align="center"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        backgroundColor: '#e8f5e8',
                                                        borderRight: index === (data.component_columns?.earnings?.length || 0) - 1 ? '2px solid #dee2e6' : '1px solid #dee2e6',
                                                        minWidth: 100
                                                    }}
                                                >
                                                    {col}
                                                </TableCell>
                                            ))}

                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    backgroundColor: '#c8e6c9',
                                                    borderRight: '2px solid #dee2e6',
                                                    minWidth: 120
                                                }}
                                            >
                                                Gross Pay
                                            </TableCell>

                                            {/* Deductions Sub-headers */}
                                            {data.component_columns?.deductions?.map((col, index) => (
                                                <TableCell
                                                    key={`ded-${index}`}
                                                    align="center"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        backgroundColor: '#ffebee',
                                                        borderRight: '1px solid #dee2e6',
                                                        minWidth: 100
                                                    }}
                                                >
                                                    {col}
                                                </TableCell>
                                            ))}

                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    backgroundColor: '#ffccbc',
                                                    borderRight: '1px solid #dee2e6',
                                                    minWidth: 120
                                                }}
                                            >
                                                Total Deduction
                                            </TableCell>

                                            <TableCell
                                                align="center"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    backgroundColor: '#bbdefb',
                                                    minWidth: 120
                                                }}
                                            >
                                                Net Pay
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {data.employees.map((emp, empIndex) => (
                                            <TableRow
                                                key={emp.emp_id}
                                                hover
                                                sx={{
                                                    '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                                                    '&:hover': { backgroundColor: '#f0f7ff' }
                                                }}
                                            >
                                                {/* Employee Info */}
                                                <TableCell
                                                    align="center"
                                                    sx={{ borderRight: '2px solid #dee2e6' }}
                                                >
                                                    {emp.emp_code}
                                                </TableCell>

                                                <TableCell
                                                    sx={{ borderRight: '2px solid #dee2e6' }}
                                                >
                                                    {emp.first_name} {emp.last_name}
                                                </TableCell>

                                                <TableCell
                                                    align="center"
                                                    sx={{ borderRight: '2px solid #dee2e6' }}
                                                >
                                                    {emp.department || '-'}
                                                </TableCell>

                                                <TableCell
                                                    align="center"
                                                    sx={{ borderRight: '2px solid #dee2e6' }}
                                                >
                                                    {emp.designation || '-'}
                                                </TableCell>

                                                {/* PD - Fixed: Shows decimals only when needed */}
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        borderRight: '1px solid #dee2e6',
                                                        backgroundColor: '#f5f5f5',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {formatDays(emp.present_days)}
                                                </TableCell>

                                                {/* ED */}
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        borderRight: '1px solid #dee2e6',
                                                        backgroundColor: '#f5f5f5',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {formatDays(emp.effective_days)}
                                                </TableCell>

                                                {/* FH */}
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        borderRight: '1px solid #dee2e6',
                                                        backgroundColor: '#e8f5e8',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {formatDays(emp.first_half_days)}
                                                </TableCell>

                                                {/* PL - Fixed: Shows decimals only when needed */}
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        borderRight: '2px solid #dee2e6',
                                                        backgroundColor: '#e3f2fd',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {formatDays(emp.paid_leaves)}
                                                </TableCell>

                                                {/* Earnings */}
                                                {data.component_columns?.earnings?.map((col, colIndex) => (
                                                    <TableCell
                                                        key={`${emp.emp_id}-earn-${colIndex}`}
                                                        align="right"
                                                        sx={{
                                                            borderRight: colIndex === (data.component_columns?.earnings?.length || 0) - 1 ? '2px solid #dee2e6' : '1px solid #dee2e6',
                                                            backgroundColor: '#f9fbe7'
                                                        }}
                                                    >
                                                        {safeFormatCurrency(emp.earnings[col])}
                                                    </TableCell>
                                                ))}

                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        backgroundColor: '#e8f5e8',
                                                        borderRight: '2px solid #dee2e6'
                                                    }}
                                                >
                                                    {safeFormatCurrency(emp.gross_pay)}
                                                </TableCell>

                                                {/* Deductions */}
                                                {data.component_columns?.deductions?.map((col, colIndex) => (
                                                    <TableCell
                                                        key={`${emp.emp_id}-ded-${colIndex}`}
                                                        align="right"
                                                        sx={{
                                                            borderRight: '1px solid #dee2e6',
                                                            backgroundColor: '#fff3e0'
                                                        }}
                                                    >
                                                        {safeFormatCurrency(emp.deductions[col])}
                                                    </TableCell>
                                                ))}

                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        backgroundColor: '#ffebee',
                                                        borderRight: '1px solid #dee2e6'
                                                    }}
                                                >
                                                    {safeFormatCurrency(emp.total_deduction)}
                                                </TableCell>

                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        backgroundColor: '#e3f2fd'
                                                    }}
                                                >
                                                    {safeFormatCurrency(emp.net_pay)}
                                                </TableCell>

                                                {/* Actions */}
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        borderLeft: '2px solid #dee2e6',
                                                        backgroundColor: '#fffde7'
                                                    }}
                                                >
                                                    <Tooltip title="View Payslip">
                                                        <IconButton
                                                            color="primary"
                                                            size="small"
                                                            onClick={() => navigate(`/payroll/payslip?emp_id=${emp.emp_id}&payroll_id=${selectedPayrollId}`)}
                                                        >
                                                            <DescriptionIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}

                                        {/* Totals Row */}
                                        <TableRow sx={{ backgroundColor: '#e0e0e0', fontWeight: 'bold' }}>
                                            <TableCell
                                                align="center"
                                                colSpan={8}
                                                sx={{ borderRight: '2px solid #dee2e6' }}
                                            >
                                                TOTAL
                                            </TableCell>

                                            {/* Earnings Totals */}
                                            {data.component_columns?.earnings?.map((col, colIndex) => {
                                                const total = data.totals?.total_by_earning_component?.[col] || 0;
                                                return (
                                                    <TableCell
                                                        key={`total-earn-${colIndex}`}
                                                        align="right"
                                                        sx={{
                                                            borderRight: colIndex === (data.component_columns?.earnings?.length || 0) - 1 ? '2px solid #dee2e6' : '1px solid #dee2e6',
                                                            backgroundColor: '#c8e6c9'
                                                        }}
                                                    >
                                                        {safeFormatCurrency(total)}
                                                    </TableCell>
                                                );
                                            })}

                                            {/* Gross Pay Total */}
                                            <TableCell
                                                align="right"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    backgroundColor: '#a5d6a7',
                                                    borderRight: '2px solid #dee2e6'
                                                }}
                                            >
                                                {safeFormatCurrency(data.totals?.total_gross)}
                                            </TableCell>

                                            {/* Deductions Totals */}
                                            {data.component_columns?.deductions?.map((col, colIndex) => {
                                                const total = data.totals?.total_by_deduction_component?.[col] || 0;
                                                return (
                                                    <TableCell
                                                        key={`total-ded-${colIndex}`}
                                                        align="right"
                                                        sx={{
                                                            borderRight: '1px solid #dee2e6',
                                                            backgroundColor: '#ffccbc'
                                                        }}
                                                    >
                                                        {safeFormatCurrency(total)}
                                                    </TableCell>
                                                );
                                            })}

                                            {/* Total Deduction Total */}
                                            <TableCell
                                                align="right"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    backgroundColor: '#ffab91',
                                                    borderRight: '1px solid #dee2e6'
                                                }}
                                            >
                                                {safeFormatCurrency(data.totals?.total_deduction)}
                                            </TableCell>

                                            {/* Net Pay Total */}
                                            <TableCell
                                                align="right"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    backgroundColor: '#90caf9',
                                                    color: '#0d47a1'
                                                }}
                                            >
                                                {safeFormatCurrency(data.totals?.total_net)}
                                            </TableCell>

                                            {/* Empty Actions Total */}
                                            <TableCell
                                                sx={{
                                                    backgroundColor: '#fff9c4',
                                                    borderLeft: '2px solid #dee2e6'
                                                }}
                                            />
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Summary Section - REMOVED Days Information */}
                            <Box sx={{
                                mt: 2,
                                p: 2,
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #dee2e6',
                                borderRadius: 1
                            }}>
                                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
                                    Payroll Summary - {data.payroll.month_year}
                                </Typography>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Card sx={{ backgroundColor: '#e3f2fd' }}>
                                            <CardContent sx={{ textAlign: 'center' }}>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Total Employees
                                                </Typography>
                                                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                                    {data.totals.total_employees}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={3}>
                                        <Card sx={{ backgroundColor: '#e8f5e8' }}>
                                            <CardContent sx={{ textAlign: 'center' }}>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Total Gross Pay
                                                </Typography>
                                                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                                                    {safeFormatCurrency(data.totals?.total_gross)}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={3}>
                                        <Card sx={{ backgroundColor: '#ffebee' }}>
                                            <CardContent sx={{ textAlign: 'center' }}>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Total Deductions
                                                </Typography>
                                                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#c62828' }}>
                                                    {safeFormatCurrency(data.totals?.total_deduction)}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={3}>
                                        <Card sx={{ backgroundColor: '#fff3e0' }}>
                                            <CardContent sx={{ textAlign: 'center' }}>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Total Net Pay
                                                </Typography>
                                                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ef6c00' }}>
                                                    {safeFormatCurrency(data.totals?.total_net)}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Box>
                        </>
                    ) : (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            No employee data found for this payroll period.
                        </Alert>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default PayRegister;